import { useState, useEffect, useCallback } from 'react';
import { tutorMatriculaService, datosSaludService } from 'services/fumService';
import { TutorMatriculaResponse, DatosSaludResponse } from 'types/fum.types';

export const useFum = (matriculaId: number | null) => {
  const [tutores, setTutores] = useState<TutorMatriculaResponse[]>([]);
  const [salud, setSalud]     = useState<DatosSaludResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [saving, setSaving]   = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const cargar = useCallback(async () => {
    if (!matriculaId) return;
    setLoading(true); setError(null);
    try {
      const [t, s] = await Promise.all([
        tutorMatriculaService.listarPorMatricula(matriculaId),
        datosSaludService.getByMatricula(matriculaId),
      ]);
      setTutores(t); setSalud(s);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar datos');
    } finally { setLoading(false); }
  }, [matriculaId]);

  useEffect(() => { cargar(); }, [cargar]);

  const agregarTutor = async (payload: Omit<import('types/fum.types').TutorMatriculaPayload, 'matriculaId'>) => {
    if (!matriculaId) return;
    setSaving(true); setSaveError(null);
    try {
      const nuevo = await tutorMatriculaService.crear({ ...payload, matriculaId });
      setTutores(prev => [...prev, nuevo]);
      setSaveSuccess(true); setTimeout(() => setSaveSuccess(false), 2000);
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : 'Error al guardar tutor');
    } finally { setSaving(false); }
  };

  const actualizarTutor = async (id: number, payload: Omit<import('types/fum.types').TutorMatriculaPayload, 'matriculaId'>) => {
    if (!matriculaId) return;
    setSaving(true); setSaveError(null);
    try {
      const updated = await tutorMatriculaService.actualizar(id, { ...payload, matriculaId });
      setTutores(prev => prev.map(t => t.idTutorMatricula === id ? updated : t));
      setSaveSuccess(true); setTimeout(() => setSaveSuccess(false), 2000);
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : 'Error al actualizar tutor');
    } finally { setSaving(false); }
  };

  const eliminarTutor = async (id: number) => {
    setSaving(true); setSaveError(null);
    try {
      await tutorMatriculaService.eliminar(id);
      setTutores(prev => prev.filter(t => t.idTutorMatricula !== id));
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : 'Error al eliminar tutor');
    } finally { setSaving(false); }
  };

  const guardarSalud = async (payload: Omit<import('types/fum.types').DatosSaludPayload, 'matriculaId'>) => {
    if (!matriculaId) return;
    setSaving(true); setSaveError(null);
    try {
      const updated = await datosSaludService.upsert({ ...payload, matriculaId });
      setSalud(updated);
      setSaveSuccess(true); setTimeout(() => setSaveSuccess(false), 2000);
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : 'Error al guardar datos de salud');
    } finally { setSaving(false); }
  };

  return {
    tutores, salud, loading, error, saving, saveError, saveSuccess,
    agregarTutor, actualizarTutor, eliminarTutor, guardarSalud, recargar: cargar,
  };
};
